import { FC, useState, useRef, useEffect, useMemo } from 'react';
import { useAuth, useAuthActions } from '@/store/authStore';
import { useLayoutActions } from '@/store/layoutStore';
import { MdOutlineMenu, MdOutlineLogout } from '@/config/icons';
import { useTranslation } from '@/hooks/useTranslation';
import Button from '@/components/Common/Button';
import LanguageSelector from '@/components/LanguageSelector';
import styles from './Header.module.css';

/**
 * Header Component
 * Top navigation bar with menu toggle, logo, and user actions
 */
const Header: FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { logout, checkAuthStatus } = useAuthActions();
  const { toggleMenu } = useLayoutActions();
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  /**
   * Wait for Zustand to hydrate from localStorage
   */
  useEffect(() => {
    // Give time for Zustand persist to hydrate
    const timer = setTimeout(() => {
      setIsHydrated(true);
      
      // If authenticated but no user, try to fetch from API
      if (isAuthenticated && !user && !isLoading) {
        checkAuthStatus();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [isAuthenticated, user, isLoading, checkAuthStatus]);

  /**
   * Get user initials for avatar
   * Memoized to recalculate only when user changes
   */
  const userInitial = useMemo(() => {
    if (!isHydrated) {
      return '...';
    }
    
    if (!user) {
      return 'U';
    }
    
    // Get first letter from firstName
    if (user.firstName) {
      return user.firstName.charAt(0).toUpperCase();
    }
    
    return 'U';
  }, [user, isAuthenticated, isLoading, isHydrated]);

  /**
   * Get user display name
   */
  const userDisplayName = useMemo(() => {
    if (!isHydrated || isLoading) return t('common.loading');
    if (!user) return 'User';
    
    // Use firstName
    if (user.firstName) return user.firstName;
    
    // Fallback to name field
    if ('name' in user && typeof (user as any).name === 'string') {
      return (user as any).name;
    }
    
    return 'User';
  }, [user, isHydrated, isLoading, t]);

  /**
   * Toggle dropdown menu
   */
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  /**
   * Handle logout
   */
  const handleLogout = () => {
    setIsDropdownOpen(false);
    logout();
  };

  /**
   * Close dropdown when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <header className={styles.header}>
      <div className={styles.left}>
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleMenu}
          aria-label="Toggle menu"
          iconLeft={<MdOutlineMenu className="w-6 h-6" />}
        />
        
        <div className={styles.logo}>
          <h1 className={styles.logoText}>ALZ Portal</h1>
        </div>
      </div>
      
      <div className={styles.right}>
        <LanguageSelector size="sm" />
        
        <div className={styles.userMenu} ref={dropdownRef}>
          <div 
            className={styles.userInfo}
            onClick={toggleDropdown}
            role="button"
            tabIndex={0}
            aria-expanded={isDropdownOpen}
            aria-haspopup="true"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleDropdown();
              }
            }}
          >
            <div className={styles.avatar} title={userDisplayName}>
              {userInitial}
            </div>
            <span className={styles.userName}>
              {userDisplayName}
            </span>
          </div>

          {isDropdownOpen && (
            <div className={styles.dropdownMenu}>
              <button
                className={styles.dropdownItem}
                onClick={handleLogout}
              >
                <MdOutlineLogout className={styles.dropdownIcon} />
                {t('auth.logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;